import React from "react";
import { useNavigate } from "react-router-dom";
import { FaPhone, FaWhatsapp, FaTools, FaClock, FaStar } from "react-icons/fa";

const HomePage = () => {
  const navigate = useNavigate();

  const handleCallNow = () => {
    window.location.href = "tel:8655557727";
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      "Hi, I need appliance service. Please assist me.",
    );
    window.open(`https://wa.me/918655557727?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="space-y-6">
              {/* Emergency Badge */}
              <div className="inline-block">
                <div
                  className="bg-red-500 text-white px-6 py-2 rounded-full font-bold text-sm shadow-lg animate-pulse"
                  data-testid="emergency-badge"
                >
                  🚨 24/7 Emergency Service Available
                </div>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                A.P. Cool Service
              </h1>

              <h2 className="text-lg sm:text-xl text-gray-700">
                Door-to-Door AC, Fridge & Washing Machine Repair
              </h2>

              <div className="bg-blue-100 border-l-4 border-blue-600 p-4 rounded">
                <p className="text-gray-800 font-medium">
                  Founded & Operated by{" "}
                  <span className="text-blue-700 font-bold">Sachin Paswan</span>
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleCallNow}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3"
                  data-testid="hero-call-now-button"
                >
                  <FaPhone /> Call Now
                </button>
                <button
                  onClick={handleWhatsApp}
                  className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3"
                  data-testid="hero-whatsapp-button"
                >
                  <FaWhatsapp /> WhatsApp Now
                </button>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/Sachin.png"
                  alt="Professional Technician"
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                <div className="flex items-center gap-3">
                  <FaStar className="text-yellow-400 text-3xl" />
                  <div>
                    <p className="font-bold text-gray-900">Trusted Service</p>
                    <p className="text-sm text-gray-600">Local & Reliable</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Why Choose A.P. Cool Service?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div
              className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              data-testid="feature-doorstep"
            >
              <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <FaTools className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Door-to-Door Service
              </h3>
              <p className="text-gray-700">
                We come to your home. No need to transport heavy appliances.
              </p>
            </div>

            <div
              className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              data-testid="feature-quick"
            >
              <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <FaClock className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Quick Response
              </h3>
              <p className="text-gray-700">
                Fast and reliable service. Same-day repairs available.
              </p>
            </div>

            <div
              className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              data-testid="feature-trusted"
            >
              <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <FaStar className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Trusted by Locals
              </h3>
              <p className="text-gray-700">
                Customer-first approach with quality service guarantee.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Services Preview */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            Our Services
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Expert repair and maintenance for all your home appliances
          </p>

          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {[
              { name: "AC Repair", icon: "❄️" },
              { name: "Fridge Repair", icon: "🧊" },
              { name: "Washing Machine", icon: "🌀" },
              { name: "Microwave", icon: "📻" },
            ].map((service) => (
              <div
                key={service.name}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 text-center"
              >
                <div className="text-5xl mb-3">{service.icon}</div>
                <h3 className="font-bold text-gray-900">{service.name}</h3>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate("/services")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid="view-all-services-button"
            >
              View All Services
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Immediate Appliance Service?
          </h2>
          <p className="text-lg mb-8 text-blue-100 max-w-2xl mx-auto">
            Call us now or send a WhatsApp message. We're ready to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:8655557727"
              className="bg-white text-blue-700 px-8 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 inline-flex items-center justify-center gap-3"
              data-testid="cta-call-button"
            >
              <FaPhone /> +91 8655557727
            </a>
            <a
              href="tel:7054773277"
              className="bg-white text-blue-700 px-8 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 inline-flex items-center justify-center gap-3"
              data-testid="cta-call-button-2"
            >
              <FaPhone /> +91 7054773277
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
